import PlainDate from '@Domain/PlainDate';
import type SubjectAddress from '@Domain/Subjects/SubjectAddress';
import AbstractFilterView, { type AbstractFilterViewArgs } from '@UI/Helpers/Filters/AbstractFilterView';
import type { DossierPeriod } from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Documents/DocumentList/Filters/Helpers/dossierPeriodOptions';
import isWithinDossierPeriod from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Documents/DocumentList/Filters/Helpers/isWithinDossierPeriod';
import type DocumentView from '@UI/Store/Documents/Views/DocumentView';

export type DocumentStatusFilter = 'all' | 'toVerify' | 'verified';

export type DocumentFilterViewArgs = AbstractFilterViewArgs & {
	readonly subject: SubjectAddress | null;
	readonly documentTypeId: number | null;
	readonly period: DossierPeriod;
	readonly status: DocumentStatusFilter;
};

/**
 * Client-side filters of the documents tab, tagged with the dossier they were set for (`belongsTo`)
 * so a value left over from another dossier is never applied.
 */
class DocumentFilterView extends AbstractFilterView<DocumentFilterViewArgs> {
	readonly subject: SubjectAddress | null;
	readonly documentTypeId: number | null;
	readonly period: DossierPeriod;
	readonly status: DocumentStatusFilter;

	constructor(args: DocumentFilterViewArgs) {
		super(args);

		this.subject = args.subject;
		this.documentTypeId = args.documentTypeId;
		this.period = args.period;
		this.status = args.status;

		Object.freeze(this);
	}

	static createDefault(subject: SubjectAddress | null): DocumentFilterView {
		return new DocumentFilterView({ subject, documentTypeId: null, period: 'all', status: 'all' });
	}

	belongsTo(subject: SubjectAddress): boolean {
		return (
			this.subject !== null &&
			this.subject.typeKey === subject.typeKey &&
			this.subject.subjectId === subject.subjectId
		);
	}

	get activeFilterCount(): number {
		return [this.documentTypeId !== null, this.period !== 'all', this.status !== 'all'].filter(Boolean)
			.length;
	}

	hasActiveFilters(): boolean {
		return this.activeFilterCount > 0;
	}

	matches(document: DocumentView, today: PlainDate): boolean {
		if (this.documentTypeId !== null && document.documentTypeId !== this.documentTypeId) {
			return false;
		}

		if (
			this.status !== 'all' &&
			(document.status.value === 'verified') !== (this.status === 'verified')
		) {
			return false;
		}

		return isWithinDossierPeriod(
			PlainDate.fromCalendar(new Date(document.createdAt)),
			this.period,
			today,
		);
	}

	setDocumentTypeId(documentTypeId: number | null): this {
		return this.cloneWith({ documentTypeId });
	}

	setStatus(status: DocumentStatusFilter): this {
		return this.cloneWith({ status });
	}

	setPeriod(period: DossierPeriod): this {
		return this.cloneWith({ period });
	}
}

export default DocumentFilterView;
